import { EditOutlined } from '@ant-design/icons';
import { Tabs } from 'components/Tabs';
import React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    hasGuideChanged,
    haveGoodToKnowItemsChanged,
    haveGuideNumberOfDaysHeaderChanged,
    haveHighlightsChanged,
    haveNumberOfDaysItemsChanged,
    haveStoriesChanged,
    isAddingGuide,
    isOrientationContentOnly,
} from 'store/guide-editor/selectors';
import GuideEditorBasicInfo from './GuideEditorBasicInfo';
import GuideEditorCollections from './GuideEditorCollections';
import GuideEditorMap from './GuideEditorMap';
import GuideEditorStory from './GuideEditorStory';
import GuideEditorTips from './GuideEditorTips';

import { TabDefinition } from 'components/Tabs/Tabs';
import { GuideEditorGoodToKnow } from './GuideEditorGoodToKnow';
import { GuideEditorHighlights } from './GuideEditorHighlights';
import { GuideEditorNumberOfDays } from './GuideEditorNumberOfDays';
import './style.css';

function GuideEditor() {
    const [searchParams, setSearchParams] = useSearchParams();
    const addingGuide = useSelector(isAddingGuide);
    const hasBasicInfoChanged = useSelector(hasGuideChanged);
    const hasStoryTabChanged = useSelector(haveStoriesChanged);
    const hasHighlightedTabChanged = useSelector(haveHighlightsChanged);
    const hasGoodToKnowTabChanged = useSelector(haveGoodToKnowItemsChanged);
    const hasNumberOfDaysTabChanged = useSelector(haveNumberOfDaysItemsChanged);
    const hasNumberOfDaysHeaderChanged = useSelector(haveGuideNumberOfDaysHeaderChanged);
    const showOnlyOrientationContent = useSelector(isOrientationContentOnly);

    const tabField = searchParams.get('tab');

    const tabs: TabDefinition[] = [
        {
            name: <>Collections</>,
            key: 'collections',
            content: <GuideEditorCollections />,
            hasFooter: false,
            isHidden: addingGuide || showOnlyOrientationContent,
        },
        {
            name: <span>Basic info {hasBasicInfoChanged && <EditOutlined className='tab-pane-icon' />}</span>,
            key: 'basicInfo',
            content: <GuideEditorBasicInfo />,
            hasFooter: false,
        },
        {
            name: <span>Story {hasStoryTabChanged && <EditOutlined className='tab-pane-icon' />}</span>,
            key: 'story',
            content: <GuideEditorStory />,
            hasFooter: false,
            isHidden: addingGuide || showOnlyOrientationContent,
        },
        {
            name: <span>Highlights {hasHighlightedTabChanged && <EditOutlined className='tab-pane-icon' />}</span>,
            key: 'highlights',
            content: <GuideEditorHighlights />,
            hasFooter: false,
            isHidden: addingGuide,
        },
        {
            name: <span>Good to know {hasGoodToKnowTabChanged && <EditOutlined className='tab-pane-icon' />}</span>,
            key: 'goodToKnow',
            content: <GuideEditorGoodToKnow />,
            hasFooter: false,
            isHidden: addingGuide,
        },
        {
            name: (
                <span>
                    Number of days{' '}
                    {(hasNumberOfDaysTabChanged || hasNumberOfDaysHeaderChanged) && (
                        <EditOutlined className='tab-pane-icon' />
                    )}
                </span>
            ),
            key: 'numberOfDays',
            content: <GuideEditorNumberOfDays />,
            hasFooter: false,
            isHidden: addingGuide,
        },

        {
            name: <>Map</>,
            key: 'map',
            content: <GuideEditorMap />,
            hasFooter: false,
            isHidden: addingGuide,
        },
        {
            name: <>Tips</>,
            key: 'tips',
            content: <GuideEditorTips />,
            hasFooter: false,
            isHidden: addingGuide || showOnlyOrientationContent,
        },
    ];

    return (
        <Tabs
            defaultActiveKey={addingGuide ? 'basicInfo' : 'collections'}
            type='full-page'
            tabs={tabs}
            activeKey={tabField || undefined}
            onChange={(newActiveKey) => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('tab', newActiveKey);
                setSearchParams(newParams, { replace: true });
            }}></Tabs>
    );
}

export default GuideEditor;
